import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { Button } from '@ds/components/core/Button';
import { Icon } from '@ds/components/core/Icon';
import { Checkbox } from '@ds/components/forms/Checkbox';
import { AuthenticatedShell, Page, SectionHead } from '@/components/AppShell';
import { EmptyState } from '@/components/EmptyState';
import { useToast } from '@/components/ToastProvider';
import { ApiError } from '@/lib/http';
import { useStatementImports, useUploadStatementImport } from '@/features/statement-imports/api';
import { StatementImportRow } from '@/features/statement-imports/components/StatementImportRow';

export const Route = createFileRoute('/_authenticated/importar')({ component: StatementImportsPage });

function StatementImportsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { data } = useStatementImports();
  const inputRef = useRef<HTMLInputElement>(null);
  // Desmarcado por padrão (0013.2): o fallback de LLM que essa checkbox liga é lento em
  // produção (Ollama CPU-only) e opcional — o match determinístico por categoryHint (0013.1)
  // já roda sempre, independente disso.
  const [categorize, setCategorize] = useState(false);

  const onError = (error: unknown) => {
    if (error instanceof ApiError) toast({ tone: 'negative', title: 'Não foi possível enviar o arquivo', description: error.message });
  };
  const upload = useUploadStatementImport({
    onSuccess: (created) => navigate({ to: '/importar/$importId', params: { importId: created.id } }),
    onError,
  });

  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) upload.mutate({ file, categorize });
  }

  const imports = data ?? [];

  return (
    <AuthenticatedShell title="Importar extrato">
      <Page>
        <div data-testid="statement-imports-page">
          <SectionHead
            title="Importar extrato"
            action={
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Checkbox
                  label="Categorizar automaticamente"
                  data-testid="statement-import-categorize-checkbox"
                  checked={categorize}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCategorize(e.target.checked)}
                />
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.ofx"
                  data-testid="statement-import-file-input"
                  style={{ display: 'none' }}
                  onChange={handleFileSelected}
                />
                <Button size="sm" iconLeft={<Icon name="upload" />} data-testid="statement-import-upload" loading={upload.isPending} onClick={() => inputRef.current?.click()}>
                  Enviar extrato
                </Button>
              </div>
            }
          />
          {imports.length === 0 ? (
            <EmptyState
              icon="upload"
              title="Nenhuma importação ainda"
              description="Envie um extrato em .csv ou .ofx pra importar várias transações de uma vez — ou mande o arquivo pro bot do Telegram."
              testId="statement-imports-empty"
            />
          ) : (
            <div>
              {imports.map((item) => (
                <StatementImportRow key={item.id} item={item} onClick={() => navigate({ to: '/importar/$importId', params: { importId: item.id } })} />
              ))}
            </div>
          )}
        </div>
      </Page>
    </AuthenticatedShell>
  );
}
